import { getSonataSession } from 'sonata-static-storage/lib/helpers/session.helpers';

import { getSharedReportSession } from '@/services/sharedReport.service';

import type { SessionEntity } from 'sonata-static-storage/lib/entities';
import type { SharedReport, SharedReportType } from 'sonata-static-storage/lib/schemas/report/sharedReport.schema';

/**
 * The on-the-fly session created when opening a shared report logs in as a single
 * dedicated system user (backend `session.service.ts`). So a shared session is
 * reliably identified by `session.user === SHARE_REPORT_USER_ID` — a persistent,
 * cookie-backed marker that survives reloads and is the same on every environment.
 * Used to contain the shared (alias) session to the D2S report and tear it down if
 * the viewer navigates anywhere else.
 */
export const SHARE_REPORT_USER_ID = '617923cf462364003756d5f3';

/** Path prefixes a shared session may stay on: the access route and the shared report itself. */
export const ALLOWED_SHARED_PATHS = ['/sharedReport', '/report/staticHtmlAI'];

/** Public link used to open a shared report on the current host. */
export function buildSharedReportLink(sharedReportId: string): string {
  return `${window.location.origin}/sharedReport/${sharedReportId}`;
}

/**
 * Returns the shared-report id when the path is the public access route
 * (`/sharedReport/:sharedReportId`), else null. Matched above the router so the
 * access modal can be shown regardless of auth state, bypassing ProtectedRoute.
 */
export function getSharedReportIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/sharedReport\/([^/?#]+)(?:\/report)?\/?$/);
  return match ? match[1] : null;
}

/** True when the current session is the fixed shared-report system session. */
export function isSharedReportSession(session: SessionEntity | null): boolean {
  return session?.toJSON?.().user === SHARE_REPORT_USER_ID;
}

/** Paths a shared session may stay on: the access route and the shared report itself. */
export function isAllowedSharedPath(pathname: string): boolean {
  return ALLOWED_SHARED_PATHS.some(prefix => pathname.startsWith(prefix));
}

/** Matches the DTS reporting route (`/reporting/driveToStore/:entityType/:entityId`). */
const DRIVE_TO_STORE_REPORT_PATH_PATTERN = /^\/reporting\/driveToStore\/[^/]+\/([^/?#]+)/;

/** True when `session` belongs to a share that persisted `sharedReportId` (backend-validated). */
export function getSessionSharedReportId(session: SessionEntity | null): string | undefined {
  return session?.toJSON?.().sharedReportId;
}

export async function getSharedReportForSession(session: SessionEntity | null): Promise<SharedReport<string> | null> {
  const sharedReportId = getSessionSharedReportId(session);
  if (!sharedReportId) {
    return null;
  }
  try {
    return await getSharedReportSession(sharedReportId, getSonataSession());
  } catch {
    return null;
  }
}

/**
 * "Full access" lets a shared session navigate between the DTS reports made up of
 * the entities included in that specific share, without ending the session — as
 * long as the target stays within `/reporting/driveToStore/...`.
 */
export async function isSharedFullAccessNavigationAllowed(
  session: SessionEntity | null,
  pathname: string
): Promise<boolean> {
  const match = pathname.match(DRIVE_TO_STORE_REPORT_PATH_PATTERN);
  if (!match) {
    return false;
  }
  const sharedReport = await getSharedReportForSession(session);
  if (!sharedReport?.fullAccess) {
    return false;
  }
  const requestedIds = match[1].split(',').filter(Boolean);
  const scopedCampaignIds = new Set((sharedReport.campaignIds ?? []).map(String));
  const scopedPoiIds = new Set((sharedReport.pois ?? []).map(String));
  return requestedIds.every(id => scopedCampaignIds.has(id) || scopedPoiIds.has(id));
}

type SharedReportRedirectInfo = {
  _id: string;
  type: SharedReportType;
};

/** Builds the real D2S report URL a shared session lands on after authenticating. */
export function buildSharedReportRedirectUrl(report: SharedReportRedirectInfo) {
  if (report.type === 'staticHtmlAI') {
    return `https://backend.sonataplatform.com/report/staticHtmlAI/${report._id}`;
  }
  return `/sharedReport/${report._id}/report`;
}

export async function getSHA512Hash(message: string) {
  // Encode the message as a UTF-8 Array
  const encoder = new TextEncoder();
  const data = encoder.encode(message);

  // Generate SHA-512 hash
  const hashBuffer = await crypto.subtle.digest('SHA-512', data);

  // Convert ArrayBuffer to a hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => `00${b.toString(16)}`.slice(-2)).join('');

  return hashHex;
}
